

interface PlayerProfit {
  id: string;
  name: string;
  netProfit: number;
}

interface CashPayment {
  playerId: string;
  amount: number;
}

interface DigitalTransfer {
  fromId: string;
  toId: string;
  amount: number;
}

interface SettlementResult {
  cashDistribution: CashPayment[];
  digitalTransfers: DigitalTransfer[];
}

export function calculateSettlement(players: PlayerProfit[], cashOnTable: number): SettlementResult {
  const cashDistribution: CashPayment[] = [];
  const digitalTransfers: DigitalTransfer[] = [];

  // Use a map to track remaining balances, ensuring we don't mutate input
  // Net profit > 0: Creditor
  // Net profit < 0: Debtor
  
  const winners = players
    .filter(p => p.netProfit > 0)
    .sort((a, b) => b.netProfit - a.netProfit)
    .map(p => ({ ...p, remainingCredit: p.netProfit }));
  
  const debtors = players
    .filter(p => p.netProfit < 0)
    .sort((a, b) => a.netProfit - b.netProfit) // Most negative first (largest debt)
    .map(p => ({ ...p, remainingDebt: Math.abs(p.netProfit) }));

  // 1. Distribute cash first (Greedy: largest winners first)
  let remainingCash = cashOnTable;
  for (const winner of winners) {
    if (remainingCash <= 0) break;
    
    const cashTaken = Math.min(winner.remainingCredit, remainingCash);
    if (cashTaken > 0) {
      cashDistribution.push({ playerId: winner.id, amount: Math.round(cashTaken * 100) / 100 });
      winner.remainingCredit -= cashTaken;
      remainingCash -= cashTaken;
    }
  }

  // 1.5. IMPORTANT: Reduce debtors' debt by the amount of cash on table.
  // We assume the cash on table was contributed by debtors.
  let remainingCashToAccountFor = cashOnTable;
  // Sort debtors by smallest debt first to settle small debts with cash? 
  // Actually, sorting doesn't matter for the total balance, but let's be consistent.
  const debtorsSortedForCash = [...debtors].sort((a, b) => a.remainingDebt - b.remainingDebt);
  for (const debtor of debtorsSortedForCash) {
    if (remainingCashToAccountFor <= 0) break;
    const cashContributed = Math.min(debtor.remainingDebt, remainingCashToAccountFor);
    debtor.remainingDebt -= cashContributed;
    remainingCashToAccountFor -= cashContributed;
  }

  // 2. Calculate digital transfers (Greedy: largest debt matches largest credit)
  const activeCreditors = winners.filter(w => w.remainingCredit > 0.001);
  const activeDebtors = debtors.filter(d => d.remainingDebt > 0.001);

  let creditorIdx = 0;
  let debtorIdx = 0;

  while (creditorIdx < activeCreditors.length && debtorIdx < activeDebtors.length) {
    const creditor = activeCreditors[creditorIdx];
    const debtor = activeDebtors[debtorIdx];

    const transferAmount = Math.min(creditor.remainingCredit, debtor.remainingDebt);
    
    if (transferAmount > 0.001) {
      digitalTransfers.push({
        fromId: debtor.id,
        toId: creditor.id,
        amount: Math.round(transferAmount * 100) / 100 // Float safety
      });

      creditor.remainingCredit -= transferAmount;
      debtor.remainingDebt -= transferAmount;
    }

    // Move to next if fully settled (using small epsilon for float safety)
    if (creditor.remainingCredit < 0.001) creditorIdx++;
    if (debtor.remainingDebt < 0.001) debtorIdx++;
  }

  return { cashDistribution, digitalTransfers };
}

interface SmartDistributePlayer {
  user_id: string;
  cash_in: number;
  total_bought_in: number;
  final_stack: number | null;
}

export function calculateSmartDistribution(
  players: SmartDistributePlayer[],
  totalCashPot: number
): Record<string, number> {
  const newCash: Record<string, number> = {};
  const limits: Record<string, number> = {};
  const currentAllocations: Record<string, number> = {};
  
  let sumBaseAllocations = 0;
  const baseAllocations: Record<string, number> = {};

  players.forEach(p => {
    const cashIn = p.cash_in || 0;
    const totalBoughtIn = p.total_bought_in || 0;
    const final_stack = p.final_stack || 0;
    
    // Step A: Calculate limit = final_stack - (total_bought_in - cash_in)
    const limit = final_stack - (totalBoughtIn - cashIn);
    limits[p.user_id] = limit;
    
    // Step B: base = limit > 0 ? Math.floor(Math.min(limit, cash_in) / 5) * 5 : 0
    const base = limit > 0 ? Math.floor(Math.min(limit, cashIn) / 5) * 5 : 0;
    baseAllocations[p.user_id] = base;
    sumBaseAllocations += base;
    
    newCash[p.user_id] = base;
    currentAllocations[p.user_id] = base;
  });

  let remainingCash = totalCashPot - sumBaseAllocations;

  // Step D: While remaining_cash >= 5
  while (remainingCash >= 5) {
    const eligiblePlayers = players
      .filter(p => currentAllocations[p.user_id] < limits[p.user_id])
      .sort((a, b) => (b.final_stack || 0) - (a.final_stack || 0));

    if (eligiblePlayers.length === 0) break;

    const topPlayer = eligiblePlayers[0];
    newCash[topPlayer.user_id] += 5;
    currentAllocations[topPlayer.user_id] += 5;
    remainingCash -= 5;
  }

  // Step E: Assign final remainder (< 5)
  if (remainingCash > 0) {
    const eligibleForRemainder = players
      .filter(p => currentAllocations[p.user_id] < limits[p.user_id])
      .sort((a, b) => (b.final_stack || 0) - (a.final_stack || 0));

    let targetPlayer;
    if (eligibleForRemainder.length > 0) {
      targetPlayer = eligibleForRemainder[0];
    } else {
      // If no one is under their limit, give it to the absolute largest stack
      targetPlayer = [...players].sort((a, b) => (b.final_stack || 0) - (a.final_stack || 0))[0];
    }

    if (targetPlayer) {
      newCash[targetPlayer.user_id] += remainingCash;
      remainingCash = 0;
    }
  }

  return newCash;
}
